// Cloudflare Web Analytics beacon.
//
// Private by default: the numbers live in Liz's Cloudflare dashboard and
// nothing about them is rendered on the site. No cookies, no local storage,
// no identifiers — so there is no consent banner to add.
//
// To wire this up, create a .env.local at the repo root with:
//   REACT_APP_CF_BEACON_TOKEN=<token from the Cloudflare Web Analytics site>
// and add the same value as a repository secret so the deploy workflow passes
// it through. CRA only inlines env vars prefixed with REACT_APP_.
//
// The token is NOT a secret. It ships in the page source by design — it only
// says which Cloudflare site a pageview belongs to. It is kept in an env var
// so the beacon is simply absent until the account exists, never so that it
// stays hidden.
//
// Host-agnostic on purpose: the beacon reports from the visitor's browser to
// Cloudflare, so it does not care whether this is served from GitHub Pages,
// Cloudflare Pages, Vercel or anywhere else. Moving hosts does not touch it.

export const BEACON_SRC = "https://static.cloudflareinsights.com/beacon.min.js";

/**
 * Append the beacon to `doc`, once, if a token was configured.
 *
 * `spa: true` is required here. This site routes on the client (/cv, /notes,
 * /projects), and without it Cloudflare only ever sees the first page a
 * visitor lands on. With it, each route change is reported — as a PAGE VIEW,
 * not a visit, so the Page Views panel is the one that answers "what are
 * people actually reading".
 *
 * Returns the script element it added, or null when there was nothing to do.
 */
export function installBeacon(doc = document, token = process.env.REACT_APP_CF_BEACON_TOKEN) {
  if (!doc || !token) return null;
  if (doc.querySelector(`script[src="${BEACON_SRC}"]`)) return null; // already installed
  const s = doc.createElement("script");
  s.src = BEACON_SRC;
  s.defer = true;
  s.setAttribute("data-cf-beacon", JSON.stringify({ token, spa: true }));
  (doc.head || doc.body || doc.documentElement).appendChild(s);
  return s;
}

export const isAnalyticsConfigured = () => Boolean(process.env.REACT_APP_CF_BEACON_TOKEN);
